import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom/dist';
import AddCategory from '../../core/modals/inventory/addcategory';
import EditCategory from '../../core/modals/inventory/editcategory';
import { userActions } from '../../core/action/userAction';
import Swal from 'sweetalert2';
import Table from '../../core/pagination/datatable'
import Select from 'react-select';
import Sliders from 'feather-icons-react/build/IconComponents/Sliders';
import { ChevronUp, Filter, PlusCircle, RotateCcw, StopCircle, Calendar, List, Eye } from 'feather-icons-react/build/IconComponents';
import { DatePicker } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ImageWithBasePath from '../../core/img/imagewithbasebath';
import { setToogleHeader } from '../../core/redux/action';
import { categoryActions } from "../../core/action/categoryaction";
import moment from 'moment';

const CategoryList = () => {
    const dataSource = useSelector((state) => state.categorylist || []);
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // Permission checks
    const canCreate = userActions.hasPermission('create-categories');
    const canEdit = userActions.hasPermission('edit-categories');
    const canDelete = userActions.hasPermission('delete-categories');
    const canView = userActions.hasPermission('view-categories');
    
    // Get user role for super admin check
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';

    // State for view modal
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // State for filters
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        date: '',
        category_type: 'parent'
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

    const oldandlatestvalue = [
        { value: "date", label: "Sort by Date" },
        { value: "newest", label: "Newest" },
        { value: "oldest", label: "Oldest" },
    ];

    const statusOptions = [
        { value: "", label: "Choose Status" },
        { value: "Active", label: "Active" },
        { value: "InActive", label: "InActive" },
    ];

    const categoryTypeOptions = [
        { value: "parent", label: "Parent Categories" },
        { value: "subcategory", label: "Subcategories" },
    ];

    // Handle add new category
    const handleAddCategory = () => {
        if (!canCreate && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to create categories',
                timer: 3000
            });
            return;
        }
        // Trigger the add category modal
        document.querySelector('[data-bs-target="#add-category"]')?.click();
    };

    // Handle view category
    const handleViewCategory = (record) => {
        if (!canView && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to view category details',
                timer: 3000
            });
            return;
        }
        setSelectedCategory(record);
        setShowViewModal(true);
    };

    // Fetch categories with useCallback
    const fetchCategories = useCallback(async () => {
        // Check if user has view permission
        if (!canView && !isSuperAdmin) {
            console.warn('User does not have permission to view categories');
            return;
        }
        
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm,
                status: filters.status,
                start_date: filters.date,
                end_date: filters.date,
                parent_id: filters.category_type === 'subcategory' ? 'all' : null,
                sort_by: sortConfig.value === 'date' ? 'created_at' : 'created_at',
                sort_order: sortConfig.value === 'oldest' ? 'ASC' : 'DESC',
                include_subcategories: true // Get subcategory names in the initial fetch
            };

            const result = await dispatch(categoryActions.fetchCategories(params));
            
            if (result.success && result.data.pagination) {
                setPagination(prev => ({
                    ...prev,
                    total: result.data.pagination.total_items || 0,
                    totalPages: result.data.pagination.total_pages || 1
                }));
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, pagination.page, pagination.limit, searchTerm, filters, sortConfig, canView, isSuperAdmin]);

    // Handle search
    const handleSearch = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchCategories();
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date) {
            handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
        } else {
            handleFilterChange('date', '');
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchCategories();
        setIsFilterVisible(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            status: '',
            date: '',
            category_type: 'parent'
        });
        setSelectedDate(null);
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchCategories();
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortConfig(option);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle edit category
    const handleEditCategory = (record) => {
        if (!canEdit && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to edit categories',
                timer: 3000
            });
            return;
        }
        dispatch({ type: "selected_category", payload: record });
    };

    // Handle delete category
    const handleDeleteCategory = (record) => {
        if (!canDelete && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to delete categories',
                timer: 3000
            });
            return;
        }
        
        // Check if category has subcategories
        if (record.subcategory_count > 0) {
            Swal.fire({
                title: 'Cannot Delete',
                text: `This category has ${record.subcategory_count} subcategories. Please delete or move subcategories first.`,
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }
        
        Swal.fire({
            title: 'Are you sure?',
            text: `Delete category "${record.category_name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const response = await dispatch(categoryActions.deleteCategory(record.id));
                if (response.success) {
                    fetchCategories();
                }
            }
        });
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchCategories();
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        setPagination(prev => ({ 
            ...prev, 
            limit: parseInt(e.target.value), 
            page: 1 
        }));
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            sorter: true,
        },
        {
            title: "Category Name",
            dataIndex: "category_name",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong>{text}</strong>
                    {record.subcategories && record.subcategories.length > 0 && (
                        <div className="subcategories-list mt-1">
                            <small className="text-muted">
                                <List size={12} className="me-1" />
                                {record.subcategory_count} subcategories
                            </small>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Category Slug",
            dataIndex: "category_slug",
            sorter: true,
        },
        {
            title: "Subcategory Names",
            dataIndex: "subcategory_names",
            render: (_, record) => {
                // Check if subcategories are available in the record
                if (record.subcategories && record.subcategories.length > 0) {
                    return (
                        <div className="subcategory-tags">
                            {record.subcategories.slice(0, 5).map((sub, index) => (
                                <span 
                                    key={index} 
                                    className={`badge me-1 mb-1 ${sub.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}
                                    title={sub.category_name}
                                >
                                    {sub.category_name}
                                </span>
                            ))}
                            {record.subcategories.length > 5 && (
                                <span className="badge bg-info">
                                    +{record.subcategories.length - 5} more
                                </span>
                            )}
                        </div>
                    );
                }
                
                // If no subcategories
                if (record.subcategory_count === 0) {
                    return <span className="text-muted">-</span>;
                }
                
                // If subcategories exist but aren't loaded yet
                return (
                    <span className="text-muted">
                        {record.subcategory_count} subcategories
                    </span>
                );
            },
        },
        {
            title: "Count",
            dataIndex: "subcategory_count",
            render: (count) => (
                <span className={`badge ${count > 0 ? 'badge-primary' : 'badge-secondary'}`}>
                    {count || 0}
                </span>
            ),
        },
        {
            title: "Created On",
            dataIndex: "created_at",
            sorter: true,
            render: (text) => text ? new Date(text).toLocaleDateString() : '-'
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span className={`badge ${text === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                    {text}
                </span>
            ),
            sorter: true,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (_text, record) => (
                <div className="edit-delete-action">
                    {/* View Button - Always show if user has view permission */}
                    {(canView || isSuperAdmin) && (
                        <button 
                            className="btn btn-sm btn-outline-info me-2 p-2" 
                            onClick={() => handleViewCategory(record)}
                            title="View Details"
                        >
                            <Eye size={14} />
                        </button>
                    )}
                    
                    {/* Edit Button - Only show if user has edit permission */}
                    {(canEdit || isSuperAdmin) && (
                        <Link 
                            className="me-2 p-2" 
                            to="#" 
                            data-bs-toggle="modal" 
                            data-bs-target="#edit-category"
                            onClick={() => handleEditCategory(record)}
                            title="Edit Category"
                        >
                            <i data-feather="edit" className="feather-edit"></i>
                        </Link>
                    )}
                    
                    {/* Delete Button - Only show if user has delete permission */}
                    {(canDelete || isSuperAdmin) && (
                        <Link 
                            className="confirm-text p-2" 
                            to="#" 
                            onClick={() => handleDeleteCategory(record)}
                            title="Delete Category"
                        >
                            <i data-feather="trash-2" className="feather-trash-2"></i>
                        </Link>
                    )}
                </div>
            )
        }
    ];

    // Tooltip functions
    const renderTooltip = (props) => (
        <Tooltip id="pdf-tooltip" {...props}>
            Pdf
        </Tooltip>
    );
    const renderExcelTooltip = (props) => (
        <Tooltip id="excel-tooltip" {...props}>
            Excel
        </Tooltip>
    );
    const renderPrinterTooltip = (props) => (
        <Tooltip id="printer-tooltip" {...props}>
            Printer
        </Tooltip>
    );
    const renderRefreshTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Refresh
        </Tooltip>
    );
    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    );

    // Initial load
    useEffect(() => {
        if (canView || isSuperAdmin) {
            fetchCategories();
        }
    }, [fetchCategories, canView, isSuperAdmin]);

    // Update when sort, page, or limit changes
    useEffect(() => {
        if (canView || isSuperAdmin) {
            fetchCategories();
        }
    }, [sortConfig, pagination.page, pagination.limit]);

    return (
        <div>
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Category List</h4>
                                <h6>Manage your categories and subcategories</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={renderTooltip}>
                                    <Link>
                                        <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                                    <Link data-bs-toggle="tooltip" data-bs-placement="top">
                                        <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderPrinterTooltip}>
                                    <Link data-bs-toggle="tooltip" data-bs-placement="top">
                                        <i data-feather="printer" className="feather-printer" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                                    <Link 
                                        data-bs-toggle="tooltip" 
                                        data-bs-placement="top" 
                                        onClick={handleRefresh}
                                    >
                                        <RotateCcw />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        
                        {/* Add New Category Button - Only show if user has create permission */}
                        {(canCreate || isSuperAdmin) && (
                            <div className="page-btn">
                                <button
                                    className="btn btn-added"
                                    onClick={handleAddCategory}
                                    data-bs-toggle="modal"
                                    data-bs-target="#add-category"
                                >
                                    <PlusCircle className="me-2" />
                                    Add New Category
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <div className="card table-list-card">
                        <div className="card-body">
                            {/* Only show filters and search if user has view permission */}
                            {(canView || isSuperAdmin) && (
                                <>
                                    <div className="table-top">
                                        <div className="search-set">
                                            <div className="search-input">
                                                <input
                                                    type="text"
                                                    placeholder="Search by category name or slug"
                                                    className="form-control form-control-sm formsearch"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                                                />
                                                <Link 
                                                    to="#" 
                                                    className="btn btn-searchset"
                                                    onClick={handleSearch}
                                                >
                                                    <i data-feather="search" className="feather-search" />
                                                </Link>
                                            </div>
                                        </div>
                                        <div className="search-path">
                                            <Link 
                                                className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                                onClick={() => setIsFilterVisible(!isFilterVisible)}
                                            >
                                                <Filter className="filter-icon" />
                                                <span>
                                                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                                                </span>
                                            </Link>
                                        </div>
                                        <div className="form-sort">
                                            <Sliders className="info-img" />
                                            <Select
                                                className="select"
                                                options={oldandlatestvalue}
                                                value={sortConfig}
                                                onChange={handleSortChange}
                                                placeholder="Newest"
                                            />
                                        </div>
                                    </div>

                                    {/* Filter Section */}
                                    <div
                                        className={`card${isFilterVisible ? " visible" : ""}`}
                                        id="filter_inputs"
                                        style={{ display: isFilterVisible ? "block" : "none" }}
                                    >
                                        <div className="card-body pb-0">
                                            <div className="row">
                                                <div className="col-lg-2 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <label>Category Type</label>
                                                        <Select
                                                            className="select"
                                                            options={categoryTypeOptions}
                                                            placeholder="Choose Type"
                                                            value={categoryTypeOptions.find(opt => opt.value === filters.category_type)}
                                                            onChange={(selected) => handleFilterChange('category_type', selected?.value || 'parent')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-2 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <StopCircle className="info-img" />
                                                        <Select
                                                            className="select"
                                                            options={statusOptions}
                                                            placeholder="Choose Status"
                                                            value={statusOptions.find(opt => opt.value === filters.status)}
                                                            onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-2 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <Calendar className="info-img" />
                                                        <div className="input-groupicon">
                                                            <DatePicker
                                                                selected={selectedDate}
                                                                onChange={handleDateChange}
                                                                className="filterdatepicker"
                                                                dateFormat="dd-MM-yyyy"
                                                                placeholder="Choose Date"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                                                    <div className="input-blocks d-flex gap-2">
                                                        <button 
                                                            className="btn btn-filters"
                                                            onClick={handleApplyFilters}
                                                        >
                                                            <i data-feather="search" className="feather-search" /> Search
                                                        </button>
                                                        <button 
                                                            className="btn btn-secondary"
                                                            onClick={handleClearFilters}
                                                        >
                                                            Clear
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Table */}
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading categories...</p>
                                    </div>
                                ) : !canView && !isSuperAdmin ? (
                                    <div className="text-center py-5">
                                        <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">Access Denied</h5>
                                        <p className="text-muted">You dont have permission to view categories</p>
                                    </div>
                                ) : dataSource.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i data-feather="folder" className="feather-folder" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">No categories found</h5>
                                        <p className="text-muted">
                                            {searchTerm || Object.values(filters).some(v => v) 
                                                ? "Try adjusting your search or filters" 
                                                : "Add your first category to get started"}
                                        </p>
                                        {(canCreate || isSuperAdmin) && (
                                            <button 
                                                className="btn btn-primary mt-3"
                                                onClick={handleAddCategory}
                                                data-bs-toggle="modal"
                                                data-bs-target="#add-category"
                                            >
                                                <PlusCircle size={16} className="me-2" />
                                                Add New Category
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <Table columns={columns} dataSource={dataSource} />
                                        
                                        {/* Pagination */}
                                        {pagination.totalPages > 1 && (
                                            <div className="pagination-wrapper mt-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="pagination-info">
                                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                                        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                    </div>
                                                    <div className="pagination-controls">
                                                        <select 
                                                            className="form-select form-select-sm me-2"
                                                            value={pagination.limit}
                                                            onChange={handleLimitChange}
                                                            style={{ width: 'auto' }}
                                                        >
                                                            <option value="10">10 per page</option>
                                                            <option value="20">20 per page</option>
                                                            <option value="50">50 per page</option>
                                                            <option value="100">100 per page</option>
                                                        </select>
                                                        
                                                        <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                    <button 
                                                                        className="page-link" 
                                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                                        disabled={pagination.page === 1}
                                                                    >
                                                                        Previous
                                                                    </button>
                                                                </li>
                                                                
                                                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                                    let pageNum;
                                                                    if (pagination.totalPages <= 5) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.page <= 3) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                                        pageNum = pagination.totalPages - 4 + i;
                                                                    } else {
                                                                        pageNum = pagination.page - 2 + i;
                                                                    }
                                                                    
                                                                    return (
                                                                        <li 
                                                                            key={pageNum} 
                                                                            className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                                                        >
                                                                            <button 
                                                                                className="page-link" 
                                                                                onClick={() => handlePageChange(pageNum)}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                                
                                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                    <button 
                                                                        className="page-link" 
                                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                                        disabled={pagination.page === pagination.totalPages}
                                                                    >
                                                                        Next
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </nav>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modals */}
            <AddCategory onSuccess={fetchCategories} />
            <EditCategory onSuccess={fetchCategories} />
            
            {/* View Category Modal */}
            {showViewModal && selectedCategory && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowViewModal(false)}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Category Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">ID:</label>
                                        <p>{selectedCategory.id}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Status:</label>
                                        <p>
                                            <span className={`badge ${selectedCategory.status === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                                                {selectedCategory.status}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Category Name:</label>
                                        <p>{selectedCategory.category_name}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Category Slug:</label>
                                        <p>{selectedCategory.category_slug}</p>
                                    </div>
                                    {selectedCategory.parent_id && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Parent Category:</label>
                                            <p>{selectedCategory.parent_name || 'N/A'}</p>
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Subcategories Count:</label>
                                        <p>{selectedCategory.subcategory_count || 0}</p>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-bold">Description:</label>
                                        <p>{selectedCategory.description || '-'}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Created On:</label>
                                        <p>{selectedCategory.created_at ? new Date(selectedCategory.created_at).toLocaleString() : '-'}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Last Updated:</label>
                                        <p>{selectedCategory.updated_at ? new Date(selectedCategory.updated_at).toLocaleString() : '-'}</p>
                                    </div>
                                </div>
                                
                                {/* Show subcategories if any */}
                                {selectedCategory.subcategories && selectedCategory.subcategories.length > 0 && (
                                    <div className="mt-3">
                                        <label className="form-label fw-bold">Subcategories:</label>
                                        <div className="subcategory-list mt-2">
                                            {selectedCategory.subcategories.map((sub, index) => (
                                                <div key={index} className="mb-2 p-2 border rounded">
                                                    <strong>{sub.category_name}</strong>
                                                    <span className={`badge ms-2 ${sub.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                                                        {sub.status}
                                                    </span>
                                                    {sub.category_slug && (
                                                        <div className="small text-muted">Slug: {sub.category_slug}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CategoryList;